import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import { mapRuleEntityToCondition, mapRuleEntityToDetail } from './mapEntityToView'
import {
  CreateTrainerCommissionRuleDto,
  TrainerCommissionClassCategoryEntity,
  TrainerCommissionCondition,
  TrainerCommissionConditionDetail,
  TrainerCommissionConductType,
  TrainerCommissionRoleContractEntity,
  TrainerCommissionRuleEntity,
  TrainerCommissionTrainingProgramEntity,
  TrainerConductConditionEntity,
  UpdateTrainerCommissionRuleDto,
} from './model'

export type { CreateTrainerCommissionRuleDto, UpdateTrainerCommissionRuleDto } from './model'

type GetTrainerCommissionConditionsParams = PaginationOpt & {
  conductType: 'training' | 'class'
  trainerId?: string | number
  trainerLevels?: string[]
}

const deserializeRoleContract = (
  item: Record<string, unknown>
): TrainerCommissionRoleContractEntity => {
  return deserialize(item, TrainerCommissionRoleContractEntity)
}

const deserializeConductCondition = (
  item: Record<string, unknown>
): TrainerConductConditionEntity => {
  return deserialize(item, TrainerConductConditionEntity)
}

const deserializeTrainerCommissionRule = (
  item: Record<string, unknown>
): TrainerCommissionRuleEntity => {
  const entity = deserialize(item, TrainerCommissionRuleEntity)

  entity.roleContracts = Array.isArray(item.roleContracts)
    ? item.roleContracts.map((rc) => deserializeRoleContract(rc as Record<string, unknown>))
    : []

  entity.classCategories = Array.isArray(item.classCategories)
    ? item.classCategories.map((c) =>
        deserialize(c as Record<string, unknown>, TrainerCommissionClassCategoryEntity)
      )
    : []

  entity.trainingPrograms = Array.isArray(item.trainingPrograms)
    ? item.trainingPrograms.map((p) =>
        deserialize(p as Record<string, unknown>, TrainerCommissionTrainingProgramEntity)
      )
    : []

  entity.conditions = Array.isArray(item.conditions)
    ? item.conditions.map((row) => deserializeConductCondition(row as Record<string, unknown>))
    : []

  return entity
}

export const TrainerCommissionService = (client: AxiosInstance) => {
  return {
    async getTrainerCommissionConditions(
      opt: GetTrainerCommissionConditionsParams
    ): Promise<PaginationResponse<TrainerCommissionCondition>> {
      const conductTypeApi =
        opt.conductType === 'class'
          ? TrainerCommissionConductType.CLASS
          : TrainerCommissionConductType.TRAINING

      const params: Record<string, unknown> = {
        page: opt.page ?? 1,
        pageSize: opt.pageSize ?? 10,
        query: opt.query,
        conductType: conductTypeApi,
      }

      if (opt.trainerId !== undefined && opt.trainerId !== '') {
        params.trainerId = opt.trainerId
      }

      if (opt.trainerLevels?.length) {
        params.trainerLevels = opt.trainerLevels.join(',')
      }

      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          '/trainer-commissions',
          { params }
        )
      )

      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data:
          data?.data?.map((item: Record<string, unknown>) =>
            mapRuleEntityToCondition(deserializeTrainerCommissionRule(item))
          ) ?? [],
      }
    },

    async getTrainerCommissionConditionById(id: string): Promise<TrainerCommissionConditionDetail> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/trainer-commissions/${id}`)
      )

      return mapRuleEntityToDetail(deserializeTrainerCommissionRule(data))
    },

    async updateTrainerCommissionConditionStatus(
      id: string,
      payload: { isActive: boolean }
    ): Promise<void> {
      await wrapApiCall(() => client.patch(`/trainer-commissions/${id}/status`, payload))
    },

    async deleteTrainerCommissionCondition(id: string): Promise<void> {
      await wrapApiCall(() => client.delete(`/trainer-commissions/${id}`))
    },

    async createTrainerCommissionCondition(
      body: CreateTrainerCommissionRuleDto
    ): Promise<TrainerCommissionRuleEntity> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/trainer-commissions', body)
      )

      return deserializeTrainerCommissionRule(data)
    },

    async updateTrainerCommissionCondition(
      id: string,
      body: UpdateTrainerCommissionRuleDto
    ): Promise<TrainerCommissionRuleEntity> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/trainer-commissions/${id}`, body)
      )

      return deserializeTrainerCommissionRule(data)
    },
  }
}
